import { twMerge } from '@/lib/classname/twMerge'
import type { PropsWithChildren, ReactElement, RefObject } from 'react'
import { DefinitionListCell } from './DefinitionListCell.client'
import { getColumnId, getColumnItems, getRowKey } from './definitionListChildren'
import type { DefinitionListItemProps } from './DefinitionListItem.client'

/**
 * Renders multi-column rows as a CSS grid.
 * Column 0 is a `<dt>`, subsequent columns are `<dd>`.
 * Designed for 2-column label/value layouts; with 3+ columns each row yields 1 dt + N dd (not a supported product use case).
 */
export function renderAlignedRows(columns: ReactElement<PropsWithChildren>[], columnCount: number) {
  const columnsWithKeys = columns.map((column, columnIndex) => ({
    columnId: getColumnId(column, columnIndex),
    items: getColumnItems(column),
  }))
  const columnItems = columnsWithKeys.map(({ items }) => items)
  const rowCount = Math.max(0, ...columnItems.map((items) => items.length))

  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const rowKey = getRowKey(columnItems, rowIndex)
    const striped = rowIndex % 2 === 0

    return (
      <div
        key={rowKey}
        className="grid gap-x-0.5"
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
        data-testid="definition-list-row"
      >
        {columnsWithKeys.map(({ columnId, items }, columnIndex) => {
          const item = items[rowIndex]
          const as = columnIndex === 0 ? 'dt' : 'dd'

          if (!item) {
            return (
              <DefinitionListCell
                key={`${rowKey}-${columnId}`}
                as={as}
                aria-hidden
                striped={striped}
                className="flex h-full items-center"
              />
            )
          }

          const {
            label: _label,
            children: itemChildren,
            className,
            isBold,
            id,
            'data-testid': dataTestId,
            style,
          } = item.props

          return (
            <DefinitionListCell
              key={`${rowKey}-${columnId}`}
              as={as}
              isBold={isBold}
              id={id}
              style={style}
              data-testid={dataTestId}
              striped={striped}
              className={twMerge(className, 'flex h-full items-center')}
            >
              {itemChildren}
            </DefinitionListCell>
          )
        })}
      </div>
    )
  })
}

type SingleColumnRenderOptions = {
  rowRefs?: RefObject<(HTMLElement | null)[]>
  rowSizes?: number[]
}

export function renderSingleColumnItems(
  items: ReactElement<DefinitionListItemProps>[],
  options?: SingleColumnRenderOptions,
) {
  return items.map((item, rowIndex) => {
    if (item.key == null) {
      throw new Error('DefinitionList.Item requires a React `key` when rendered in a list')
    }

    const rowKey = String(item.key)
    const striped = rowIndex % 2 === 0
    const {
      label,
      children: itemChildren,
      className,
      isBold,
      id,
      'data-testid': dataTestId,
      style,
    } = item.props
    const minHeight = options?.rowSizes?.[rowIndex]
    const rowRefs = options?.rowRefs
    const rowRef = rowRefs
      ? (element: HTMLElement | null) => {
          rowRefs.current[rowIndex] = element
        }
      : undefined

    if (label) {
      return (
        <div
          key={rowKey}
          ref={rowRef}
          style={minHeight ? { ...style, minHeight } : style}
          className={twMerge(
            'm-0 flex flex-col justify-center gap-0.5 rounded-xs px-2 py-0.5 leading-light text-brand-2-900',
            striped && 'bg-brand-2-50',
          )}
          data-testid="definition-list-labeled-row"
        >
          <DefinitionListCell
            as="dt"
            isBold
            striped={false}
            className="rounded-none px-0 py-0 text-md desktop:sr-only"
          >
            {label}
          </DefinitionListCell>
          <DefinitionListCell
            as="dd"
            isBold={isBold}
            id={id}
            data-testid={dataTestId}
            striped={false}
            className={twMerge('rounded-none px-0 py-0', className)}
          >
            {itemChildren}
          </DefinitionListCell>
        </div>
      )
    }

    return (
      <DefinitionListCell
        key={rowKey}
        as="dd"
        ref={rowRef}
        isBold={isBold}
        id={id}
        style={minHeight ? { ...style, minHeight } : style}
        data-testid={dataTestId}
        striped={striped}
        className={className}
      >
        {itemChildren}
      </DefinitionListCell>
    )
  })
}
